import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { Search, MapPin, Star, Briefcase, Clock, DollarSign, Filter, ChevronDown, ArrowLeft, Users, Calendar, Sparkles, X, Megaphone, ExternalLink, Award, Globe, GitCompare, Bookmark } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth, API } from '../App';
import axios from 'axios';
import { REGIONS, LANGUAGES, SKILLS, INDUSTRIES, getLanguageForCountry, getMainCategories, getSubCategories, getSubSubCategories, getTrainingTypes } from '../data/globalData';
import { ProfileImage } from '../components/ProfileImage';
import { SEO } from '../components/SEO';
import { useCurrency, CurrencySelector } from '../components/CurrencyProvider';
import { toast } from 'sonner';

const BADGE_STYLES_COMPACT = {
  'Top Rated': 'bg-amber-100 text-amber-700',
  'Elite Trainer': 'bg-purple-100 text-purple-700',
  'Verified Profile': 'bg-emerald-100 text-emerald-700',
};
const getBadgeStyleCompact = (badge) => BADGE_STYLES_COMPACT[badge] || 'bg-blue-100 text-blue-700';

export default function SearchResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const { format: formatCurrency } = useCurrency();
  
  // 3-tier training category state
  const [mainCategory, setMainCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [subSubCategory, setSubSubCategory] = useState('');
  
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [ads, setAds] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [savedSearches, setSavedSearches] = useState([]);
  const [compareIds, setCompareIds] = useState(new Set());
  const [compareData, setCompareData] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [searchType, setSearchType] = useState(searchParams.get('type') || 'trainers');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('query') || searchParams.get('nl_query') || '');
  const [nlQuery, setNlQuery] = useState(searchParams.get('nl_query') || '');
  const [parsedFilters, setParsedFilters] = useState(location.state?.parsedFilters || null);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [filters, setFilters] = useState({
    skill: searchParams.get('skill') || '',
    location: searchParams.get('location') || '',
    industry: searchParams.get('industry') || '',
    training_type: searchParams.get('training_type') || '',
    experience_min: searchParams.get('experience_min') || '',
    experience_max: searchParams.get('experience_max') || '',
    rate_min: searchParams.get('rate_min') || '',
    rate_max: searchParams.get('rate_max') || '',
    rating_min: searchParams.get('rating_min') || '',
    language: searchParams.get('language') || 'English'
  });

  // Get cities for selected country
  const getCitiesForCountry = (country) => {
    if (!country) return [];
    for (const [region, countries] of Object.entries(REGIONS)) {
      if (countries[country]) {
        return countries[country];
      }
    }
    return [];
  };

  // Get all countries grouped by region
  const getCountriesByRegion = () => {
    const result = {};
    Object.entries(REGIONS).forEach(([region, countries]) => {
      result[region] = Object.keys(countries);
    });
    return result;
  };

  // Handle country change - auto-select language
  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setFilters(prev => ({
      ...prev,
      location: '',
      language: getLanguageForCountry(country)
    }));
  };

  // Define useCallback functions BEFORE the useEffect that uses them
  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      const query = searchParams.get('query');
      if (query) params.set('query', query);
      
      const skills = searchParams.get('skills');
      if (skills) params.set('skills', skills);
      
      const loc = searchParams.get('location');
      if (loc) params.set('location', loc);
      
      const industry = searchParams.get('industry');
      if (industry) params.set('industry', industry);
      
      const trainingType = searchParams.get('training_type');
      if (trainingType) params.set('training_type', trainingType);
      
      const expMin = searchParams.get('experience_min');
      if (expMin) params.set('experience_min', expMin);
      
      const expMax = searchParams.get('experience_max');
      if (expMax) params.set('experience_max', expMax);
      
      const rateMin = searchParams.get('rate_min');
      if (rateMin) params.set('rate_min', rateMin);
      
      const rateMax = searchParams.get('rate_max');
      if (rateMax) params.set('rate_max', rateMax);
      
      const ratingMin = searchParams.get('rating_min');
      if (ratingMin) params.set('rating_min', ratingMin);

      const lang = searchParams.get('language');
      if (lang) params.set('language', lang);

      const type = searchParams.get('type') || 'trainers';
      const endpoint = type === 'jobs' ? '/jobs' : '/trainers';
      
      const response = await axios.get(`${API}${endpoint}?${params.toString()}`);
      setResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  const fetchAds = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ads/active`);
      setAds(res.data);
    } catch (e) { /* silent */ }
  }, []);

  const fetchSavedSearches = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/saved-searches`, { headers: { Authorization: `Bearer ${token}` } });
      setSavedSearches(res.data);
    } catch (e) { /* silent */ }
  }, [token]);

  // Now the useEffect can safely reference the useCallback functions
  useEffect(() => {
    // If we have NL results from navigation state, use them
    if (location.state?.nlResults) {
      setResults(location.state.nlResults);
      setParsedFilters(location.state.parsedFilters);
      setNlQuery(location.state.originalQuery);
      setLoading(false);
    } else {
      fetchResults();
    }
    fetchAds();
    fetchSavedSearches();
  }, [searchParams, fetchResults, fetchAds, fetchSavedSearches, location.state]);

  useEffect(() => {
    // Clear NL results when filters change manually
    if (!location.state?.nlResults) {
      setParsedFilters(null);
      setNlQuery('');
    }
  }, [filters, location.state?.nlResults]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    params.set('type', searchType);
    if (searchQuery) params.set('query', searchQuery);
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && (Array.isArray(value) ? value.length > 0 : true)) {
        params.set(key, Array.isArray(value) ? value.join(',') : value);
      }
    });
    
    setSearchParams(params);
  };

  const toggleSkill = (skill) => {
    setFilters(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  const clearFilters = () => {
    setFilters({
      skill: '',
      location: '',
      industry: '',
      training_type: '',
      experience_min: '',
      experience_max: '',
      rate_min: '',
      rate_max: '',
      rating_min: '',
      language: 'English'
    });
    setSelectedCountry('');
    setSearchQuery('');
    setSearchParams({ type: searchType });
  };

  const handleSaveSearch = async () => {
    if (!token) { toast.error('Please login to save searches'); return; }
    const name = window.prompt('Name this saved search:', `${searchType} search`);
    if (!name) return;
    try {
      await axios.post(`${API}/saved-searches`, {
        name, search_type: searchType, filters: { ...filters, query: searchQuery }
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Search saved!');
      fetchSavedSearches();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleLoadSearch = (saved) => {
    const f = saved.filters || {};
    setSearchType(saved.search_type);
    setSearchQuery(f.query || '');
    setFilters(prev => ({ ...prev, ...f }));
    setTimeout(() => handleSearch(), 200);
  };

  const handleDeleteSavedSearch = async (id) => {
    try {
      await axios.delete(`${API}/saved-searches/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Saved search deleted');
      fetchSavedSearches();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleCompare = (trainerId) => {
    setCompareIds(prev => {
      const next = new Set(prev);
      if (next.has(trainerId)) { next.delete(trainerId); } else {
        if (next.size >= 5) { toast.error('Maximum 5 trainers for comparison'); return prev; }
        next.add(trainerId);
      }
      return next;
    });
  };

  const handleCompare = async () => {
    if (compareIds.size < 2) { toast.error('Select at least 2 trainers to compare'); return; }
    try {
      const res = await axios.get(`${API}/trainers/compare?ids=${[...compareIds].join(',')}`);
      setCompareData(res.data);
      setShowCompare(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to load comparison'); }
  };

  // Fetch bookmarked IDs
  useEffect(() => {
    if (token && user?.user_type === 'corporate') {
      axios.get(`${API}/bookmarks/ids`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setBookmarkedIds(new Set(res.data)))
        .catch(() => {});
    }
  }, [token, user]);

  const toggleBookmark = async (trainerId) => {
    if (!token) { toast.error('Please sign in to bookmark'); return; }
    try {
      if (bookmarkedIds.has(trainerId)) {
        await axios.delete(`${API}/bookmarks/${trainerId}`, { headers: { Authorization: `Bearer ${token}` } });
        setBookmarkedIds(prev => { const n = new Set(prev); n.delete(trainerId); return n; });
        toast.success('Removed from shortlist');
      } else {
        await axios.post(`${API}/bookmarks/${trainerId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setBookmarkedIds(prev => new Set([...prev, trainerId]));
        toast.success('Added to shortlist');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="search-results-page">
      <SEO
        title={searchType === 'trainers' ? 'Find Expert Trainers' : 'Browse Training Requirements'}
        description={searchType === 'trainers'
          ? 'Search and find expert corporate trainers across Leadership, Technology, Sales, Compliance & more. Filter by skill, location, experience, and rating.'
          : 'Browse open training requirements from top companies. Apply as a trainer and grow your career.'}
        path={`/search?type=${searchType}`}
      />
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <span className="font-outfit font-bold text-xl text-slate-900 hidden sm:block">Training Solutions</span>
            </Link>

            {/* Search Bar */}
            <div className="flex-1 max-w-2xl">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder={searchType === 'jobs' ? 'Search training requirements...' : 'Search trainers...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full h-10 pl-10 pr-4 rounded-full border border-slate-200"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Search Button */}
            <Button
              onClick={handleSearch}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6"
              data-testid="search-btn"
            >
              Search
            </Button>

            <CurrencySelector />

            {token && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveSearch}
                className="text-slate-600 shrink-0"
                data-testid="save-search-btn"
              >
                <Star className="w-4 h-4 mr-1" />
                Save
              </Button>
            )}

            {user && (
              <Button
                variant="ghost"
                onClick={() => navigate(user.user_type === 'trainer' ? '/trainer/dashboard' : '/corporate/dashboard')}
                data-testid="dashboard-btn"
              >
                Dashboard
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Filter Chips */}
        {searchType === 'trainers' && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="quick-filter-chips">
            {[
              { label: 'Under ₹500/hr', key: 'rate_max', value: '500', icon: <DollarSign className="w-3 h-3" /> },
              { label: '₹500-2000/hr', key: 'rate_range', value: '500-2000', icon: <DollarSign className="w-3 h-3" /> },
              { label: '₹2000+/hr', key: 'rate_min', value: '2000', icon: <DollarSign className="w-3 h-3" /> },
              { label: '4+ Stars', key: 'rating_min', value: '4', icon: <Star className="w-3 h-3" /> },
              { label: '4.5+ Stars', key: 'rating_min', value: '4.5', icon: <Star className="w-3 h-3" /> },
              { label: '5+ Yrs Exp', key: 'experience_min', value: '5', icon: <Clock className="w-3 h-3" /> },
              { label: '10+ Yrs Exp', key: 'experience_min', value: '10', icon: <Clock className="w-3 h-3" /> },
              { label: 'English', key: 'language', value: 'English', icon: <Globe className="w-3 h-3" /> },
              { label: 'Hindi', key: 'language', value: 'Hindi', icon: <Globe className="w-3 h-3" /> },
              { label: 'Spanish', key: 'language', value: 'Spanish', icon: <Globe className="w-3 h-3" /> },
            ].map(chip => {
              const isActive = chip.key === 'rate_range'
                ? (filters.rate_min === '500' && filters.rate_max === '2000')
                : filters[chip.key] === chip.value;
              return (
                <button
                  key={chip.label}
                  onClick={() => {
                    if (chip.key === 'rate_range') {
                      if (isActive) {
                        setFilters(prev => ({ ...prev, rate_min: '', rate_max: '' }));
                      } else {
                        setFilters(prev => ({ ...prev, rate_min: '500', rate_max: '2000' }));
                      }
                    } else {
                      setFilters(prev => ({
                        ...prev,
                        [chip.key]: isActive ? '' : chip.value
                      }));
                    }
                    setTimeout(() => handleSearch(), 100);
                  }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    isActive
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                  data-testid={`quick-filter-${chip.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                >
                  {chip.icon}
                  {chip.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Saved Searches */}
        {savedSearches.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="saved-searches">
            <span className="text-xs font-medium text-slate-400 mr-1">Saved:</span>
            {savedSearches.map(s => (
              <div key={s.id} className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full pl-3 pr-1 py-1">
                <button
                  onClick={() => handleLoadSearch(s)}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  data-testid={`load-search-${s.id}`}
                >
                  {s.name}
                </button>
                <button
                  onClick={() => handleDeleteSavedSearch(s.id)}
                  className="text-slate-300 hover:text-red-500 p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-8">
          {/* Sidebar Filters */}
          <aside className="w-64 hidden lg:block flex-shrink-0">
            <div className="bg-white rounded-xl border border-slate-200 p-6 sticky top-24" data-testid="filters-sidebar">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-outfit font-semibold text-lg text-slate-900">Filters</h3>
                <button
                  onClick={clearFilters}
                  className="text-sm text-blue-600 hover:text-blue-700"
                  data-testid="clear-filters-btn"
                >
                  Clear all
                </button>
              </div>

              {/* Search Type Toggle */}
              <div className="mb-6">
                <label className="text-sm font-medium text-slate-700 mb-2 block">Search For</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSearchType('trainers')}
                    className={`flex-1 py-2 px-3 text-sm rounded-lg border transition-all ${
                      searchType === 'trainers'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="type-trainers-btn"
                  >
                    Trainers
                  </button>
                  <button
                    onClick={() => setSearchType('jobs')}
                    className={`flex-1 py-2 px-3 text-sm rounded-lg border transition-all ${
                      searchType === 'jobs'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="type-jobs-btn"
                  >
                    Requirements
                  </button>
                </div>
              </div>

              {/* Skill Dropdown */}
              <div className="mb-6">
                <label className="text-sm font-medium text-slate-700 mb-2 block">Skill</label>
                <select
                  value={filters.skill}
                  onChange={(e) => setFilters(prev => ({ ...prev, skill: e.target.value }))}
                  className="w-full h-9 px-3 text-sm rounded-lg border border-slate-200"
                  data-testid="filter-skill"
                >
                  <option value="">Any Skill</option>
                  {SKILLS.map(skill => (
                    <option key={skill} value={skill}>{skill}</option>
                  ))}
                </select>
              </div>

              {/* Location */}
              <div className="mb-6">
                <label className="text-sm font-medium text-slate-700 mb-2 block">Country</label>
                <select
                  value={selectedCountry}
                  onChange={(e) => handleCountryChange(e.target.value)}
                  className="w-full h-9 px-3 text-sm rounded-lg border border-slate-200"
                  data-testid="filter-country"
                >
                  <option value="">Any Country</option>
                  {Object.entries(getCountriesByRegion()).map(([region, countries]) => (
                    <optgroup key={region} label={`── ${region} ──`}>
                      {countries.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              {/* City */}
              <div className="mb-6">
                <label className="text-sm font-medium text-slate-700 mb-2 block">City</label>
                <select
                  value={filters.location}
                  onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full h-9 px-3 text-sm rounded-lg border border-slate-200"
                  data-testid="filter-location"
                  disabled={!selectedCountry}
                >
                  <option value="">{selectedCountry ? 'Any City' : 'Select Country First'}</option>
                  {getCitiesForCountry(selectedCountry).map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>

              {/* Industry */}
              <div className="mb-6">
                <label className="text-sm font-medium text-slate-700 mb-2 block">Industry</label>
                <select
                  value={filters.industry}
                  onChange={(e) => setFilters(prev => ({ ...prev, industry: e.target.value }))}
                  className="w-full h-9 px-3 text-sm rounded-lg border border-slate-200"
                  data-testid="filter-industry"
                >
                  <option value="">Any Industry</option>
                  {INDUSTRIES.map(ind => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>

              {/* 3-Tier Training Category */}
              <div className="mb-6">
                <label className="text-sm font-medium text-slate-700 mb-2 block">Training Category</label>
                <select
                  value={mainCategory}
                  onChange={(e) => {
                    setMainCategory(e.target.value);
                    setSubCategory('');
                    setSubSubCategory('');
                    setFilters(prev => ({ ...prev, training_type: '' }));
                  }}
                  className="w-full h-9 px-3 text-sm rounded-lg border border-slate-200 mb-2"
                  data-testid="filter-main-category"
                >
                  <option value="">Main Category</option>
                  {getMainCategories().map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <select
                  value={subCategory}
                  onChange={(e) => {
                    setSubCategory(e.target.value);
                    setSubSubCategory('');
                    setFilters(prev => ({ ...prev, training_type: '' }));
                  }}
                  className="w-full h-9 px-3 text-sm rounded-lg border border-slate-200 mb-2"
                  disabled={!mainCategory}
                >
                  <option value="">{mainCategory ? 'Sub Category' : 'Select Main First'}</option>
                  {getSubCategories(mainCategory).map(sub => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </select>
                <select
                  value={subSubCategory}
                  onChange={(e) => {
                    setSubSubCategory(e.target.value);
                    setFilters(prev => ({ ...prev, training_type: '' }));
                  }}
                  className="w-full h-9 px-3 text-sm rounded-lg border border-slate-200 mb-2"
                  disabled={!subCategory}
                >
                  <option value="">{subCategory ? 'Training Area' : 'Select Sub First'}</option>
                  {getSubSubCategories(mainCategory, subCategory).map(subSub => (
                    <option key={subSub} value={subSub}>{subSub}</option>
                  ))}
                </select>
                <select
                  value={filters.training_type}
                  onChange={(e) => setFilters(prev => ({ ...prev, training_type: e.target.value }))}
                  className="w-full h-9 px-3 text-sm rounded-lg border border-slate-200"
                  data-testid="filter-training-type"
                  disabled={!subSubCategory}
                >
                  <option value="">{subSubCategory ? 'Specific Training' : 'Select Area First'}</option>
                  {getTrainingTypes(mainCategory, subCategory, subSubCategory).map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {searchType === 'trainers' && (
                <>
                  {/* Experience */}
                  <div className="mb-6">
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Experience (Years)</label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={filters.experience_min}
                        onChange={(e) => setFilters(prev => ({ ...prev, experience_min: e.target.value }))}
                        className="w-full h-9 text-sm"
                      />
                      <Input
                        type="number"
                        placeholder="Max"
                        value={filters.experience_max}
                        onChange={(e) => setFilters(prev => ({ ...prev, experience_max: e.target.value }))}
                        className="w-full h-9 text-sm"
                      />
                    </div>
                  </div>

                  {/* Rate */}
                  <div className="mb-6">
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Hourly Rate (USD)</label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={filters.rate_min}
                        onChange={(e) => setFilters(prev => ({ ...prev, rate_min: e.target.value }))}
                        className="w-full h-9 text-sm"
                      />
                      <Input
                        type="number"
                        placeholder="Max"
                        value={filters.rate_max}
                        onChange={(e) => setFilters(prev => ({ ...prev, rate_max: e.target.value }))}
                        className="w-full h-9 text-sm"
                      />
                    </div>
                  </div>

                  {/* Rating */}
                  <div className="mb-6">
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Minimum Rating</label>
                    <select
                      value={filters.rating_min}
                      onChange={(e) => setFilters(prev => ({ ...prev, rating_min: e.target.value }))}
                      className="w-full h-9 px-3 text-sm rounded-lg border border-slate-200"
                      data-testid="filter-rating"
                    >
                      <option value="">Any Rating</option>
                      <option value="3">3+ Stars</option>
                      <option value="4">4+ Stars</option>
                      <option value="4.5">4.5+ Stars</option>
                    </select>
                  </div>

                  {/* Language */}
                  <div className="mb-6">
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Language</label>
                    <select
                      value={filters.language}
                      onChange={(e) => setFilters(prev => ({ ...prev, language: e.target.value }))}
                      className="w-full h-9 px-3 text-sm rounded-lg border border-slate-200"
                      data-testid="filter-language"
                    >
                      <option value="">Any Language</option>
                      {LANGUAGES.map(lang => (
                        <option key={lang} value={lang}>{lang}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <Button
                onClick={handleSearch}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="apply-filters-btn"
              >
                Apply Filters
              </Button>
            </div>
          </aside>

          {/* Results */}
          <main className="flex-1">
            {/* Mobile Filter Toggle */}
            <div className="lg:hidden mb-4">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="w-full"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </Button>
            </div>

            {/* AI Search Info Banner */}
            {nlQuery && parsedFilters && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6" data-testid="ai-search-banner">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-purple-900 font-medium text-sm">AI understood your search:</p>
                      <p className="text-purple-700 text-sm italic mb-2">&ldquo;{nlQuery}&rdquo;</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(parsedFilters).map(([key, value]) => (
                          <span 
                            key={key} 
                            className="px-2 py-1 bg-white border border-purple-200 rounded-full text-xs text-purple-700"
                          >
                            <span className="font-medium">{key.replace(/_/g, ' ')}:</span> {Array.isArray(value) ? value.join(', ') : String(value)}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <button 
                    onClick={() => {
                      setNlQuery('');
                      setParsedFilters(null);
                      navigate('/search?type=' + searchType);
                    }}
                    className="text-purple-400 hover:text-purple-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-outfit text-2xl font-semibold text-slate-900">
                {loading ? 'Searching...' : `${results.length} ${searchType === 'jobs' ? 'Requirements' : 'Trainers'} Found`}
              </h2>
            </div>

            {/* Sponsored Ad Banner */}
            {ads.length > 0 && (
              <div className="mb-6 space-y-3" data-testid="ads-banner-section">
                {ads.slice(0, 2).map(ad => (
                  <div
                    key={ad.id}
                    className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => ad.link_url && window.open(ad.link_url, '_blank')}
                    data-testid={`ad-banner-${ad.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                        {ad.company_name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-blue-500 bg-blue-100 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Megaphone className="w-3 h-3" /> Sponsored
                          </span>
                          <span className="text-xs text-slate-400">{ad.company_name}</span>
                        </div>
                        <h4 className="font-outfit font-semibold text-slate-900 mb-1">{ad.headline}</h4>
                        <p className="text-sm text-slate-600 line-clamp-1">{ad.description}</p>
                        {ad.training_types?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {ad.training_types.slice(0, 4).map(t => (
                              <span key={t} className="px-2 py-0.5 bg-white text-blue-700 text-xs rounded-full border border-blue-100">{t}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      {ad.link_url && (
                        <ExternalLink className="w-4 h-4 text-blue-400 flex-shrink-0 mt-1" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Results Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 bg-slate-200 rounded-xl"></div>
                      <div className="flex-1">
                        <div className="h-5 bg-slate-200 rounded w-3/4 mb-2"></div>
                        <div className="h-4 bg-slate-200 rounded w-1/2 mb-4"></div>
                        <div className="flex gap-2">
                          <div className="h-6 bg-slate-200 rounded w-16"></div>
                          <div className="h-6 bg-slate-200 rounded w-16"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : results.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-2">No results found</h3>
                <p className="text-slate-500 mb-6">Try adjusting your search criteria or filters</p>
                <Button onClick={clearFilters} variant="outline">
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="results-grid">
                {searchType === 'trainers' ? (
                  results.map((trainer) => (
                    <TrainerCard key={trainer.id} trainer={trainer} onClick={() => navigate(`/trainer/${trainer.id}`)} isCompareSelected={compareIds.has(trainer.id)} onToggleCompare={() => toggleCompare(trainer.id)} isBookmarked={bookmarkedIds.has(trainer.id)} onToggleBookmark={() => toggleBookmark(trainer.id)} showBookmark={user?.user_type === 'corporate'} />
                  ))
                ) : (
                  results.map((job) => (
                    <JobCard key={job.id} job={job} onClick={() => navigate(`/job/${job.id}`)} />
                  ))
                )}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Floating Compare Bar */}
      {searchType === 'trainers' && compareIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-4 z-50" data-testid="compare-bar">
          <GitCompare className="w-5 h-5" />
          <span className="text-sm font-medium">{compareIds.size} trainer{compareIds.size > 1 ? 's' : ''} selected</span>
          <Button size="sm" onClick={handleCompare} disabled={compareIds.size < 2} className="bg-blue-600 hover:bg-blue-700 text-white h-8 text-xs" data-testid="compare-btn">
            Compare Now
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setCompareIds(new Set())} className="text-white hover:text-slate-300 h-8 text-xs">
            Clear
          </Button>
        </div>
      )}

      {/* Comparison Modal */}
      {showCompare && compareData && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCompare(false)}>
          <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[85vh] overflow-auto" onClick={e => e.stopPropagation()} data-testid="compare-modal">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-5 flex items-center justify-between rounded-t-2xl">
              <h2 className="font-outfit text-xl font-bold text-slate-900">Trainer Comparison</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowCompare(false)} data-testid="close-compare"><X className="w-5 h-5" /></Button>
            </div>
            <div className="p-5">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="text-left p-3 text-sm font-medium text-slate-500 bg-slate-50 rounded-tl-lg w-36">Attribute</th>
                      {compareData.map(t => (
                        <th key={t.id} className="p-3 text-center bg-slate-50 min-w-[180px]">
                          <div className="flex flex-col items-center gap-2">
                            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">{t.name?.charAt(0)}</div>
                            <span className="font-semibold text-slate-900 text-sm cursor-pointer hover:text-blue-600" onClick={() => { setShowCompare(false); navigate(`/trainer/${t.id}`); }}>{t.name}</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[
                      { label: 'Location', key: 'location', icon: '📍' },
                      { label: 'Experience', render: t => `${t.experience_years} years` },
                      { label: 'Hourly Rate', render: t => `₹${t.hourly_rate?.toLocaleString()}/hr` },
                      { label: 'Rating', render: t => <span className="flex items-center justify-center gap-1"><Star className="w-4 h-4 text-amber-400 fill-amber-400" />{t.rating?.toFixed(1) || '0.0'} ({t.total_reviews})</span> },
                      { label: 'Skills', render: t => <div className="flex flex-wrap justify-center gap-1">{(t.skills||[]).slice(0,4).map(s => <span key={s} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">{s}</span>)}</div> },
                      { label: 'Industries', render: t => (t.industry||[]).join(', ') || '-' },
                      { label: 'Languages', render: t => (t.languages||[]).join(', ') || '-' },
                      { label: 'Completed', render: t => `${t.completed_trainings || 0} trainings` },
                      { label: 'Portfolio', render: t => `${t.portfolio_count || 0} items` },
                      { label: 'Total Bookings', render: t => t.total_bookings || 0 },
                    ].map((row) => (
                      <tr key={row.label} className={row.label === 'Skills' || row.label === 'Languages' || row.label === 'Portfolio' ? 'bg-slate-50/50' : 'bg-white'}>
                        <td className="p-3 text-sm font-medium text-slate-700">{row.label}</td>
                        {compareData.map(t => (
                          <td key={t.id} className="p-3 text-sm text-slate-600 text-center">
                            {row.render ? row.render(t) : t[row.key] || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Trainer Card Component
const TrainerCard = ({ trainer, onClick, isCompareSelected, onToggleCompare, isBookmarked, onToggleBookmark, showBookmark }) => {
  const { format: formatCurrency } = useCurrency();
  return (
  <div
    className={`bg-white rounded-xl border p-6 hover:shadow-lg transition-all cursor-pointer ${isCompareSelected ? 'border-blue-400 ring-2 ring-blue-100' : 'border-slate-200 hover:border-blue-200'}`}
    data-testid={`trainer-card-${trainer.id}`}
  >
    <div className="flex items-start gap-4">
      <div className="relative">
        <ProfileImage imagePath={trainer.avatar} name={trainer.name} size="md" />
        {onToggleCompare && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleCompare(); }}
            className={`absolute -top-1 -left-1 w-5 h-5 rounded border-2 flex items-center justify-center text-xs transition-colors ${
              isCompareSelected ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-300 hover:border-blue-400'
            }`}
            data-testid={`compare-check-${trainer.id}`}
            title="Add to comparison"
          >
            {isCompareSelected && '✓'}
          </button>
        )}
      </div>
      <div className="flex-1 min-w-0" onClick={onClick}>
        <div className="flex items-start justify-between">
          <h3 className="font-outfit font-semibold text-lg text-slate-900 truncate">{trainer.name}</h3>
          <div className="flex items-center gap-2 ml-2 flex-shrink-0">
            {trainer.badges?.length > 0 && (
              <div className="flex gap-1">
                {trainer.badges.slice(0, 2).map((badge) => (
                  <span key={badge} className={`px-2 py-0.5 text-xs rounded-full font-medium ${getBadgeStyleCompact(badge)}`}>
                    {badge}
                  </span>
                ))}
              </div>
            )}
            {showBookmark && (
              <button
                onClick={(e) => { e.stopPropagation(); onToggleBookmark(); }}
                className={`p-1 rounded-lg transition-colors ${isBookmarked ? 'text-amber-500 hover:text-amber-600' : 'text-slate-300 hover:text-amber-400'}`}
                data-testid={`bookmark-${trainer.id}`}
                title={isBookmarked ? 'Remove from shortlist' : 'Add to shortlist'}
              >
                <Bookmark className={`w-5 h-5 ${isBookmarked ? 'fill-amber-500' : ''}`} />
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
          {trainer.location && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {trainer.location}
            </span>
          )}
          {trainer.experience_years > 0 && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {trainer.experience_years}+ yrs
            </span>
          )}
        </div>
        
        {trainer.skills?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {trainer.skills.slice(0, 3).map(skill => (
              <span key={skill} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full">
                {skill}
              </span>
            ))}
            {trainer.skills.length > 3 && (
              <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded-full">
                +{trainer.skills.length - 3}
              </span>
            )}
          </div>
        )}

        {trainer.languages?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {trainer.languages.slice(0, 3).map(lang => (
              <span key={lang} className="px-2 py-0.5 bg-teal-50 text-teal-700 text-xs rounded-full">
                {lang}
              </span>
            ))}
            {trainer.languages.length > 3 && (
              <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded-full">
                +{trainer.languages.length - 3} languages
              </span>
            )}
          </div>
        )}

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
            <span className="font-medium text-slate-900">{trainer.rating?.toFixed(1) || '0.0'}</span>
            <span className="text-slate-400 text-sm">({trainer.total_reviews || 0})</span>
          </div>
          {trainer.hourly_rate > 0 && (
            <span className="font-semibold text-slate-900">{formatCurrency(trainer.hourly_rate)}/hr</span>
          )}
        </div>
      </div>
    </div>
  </div>
);
};

// Job Card Component
const JobCard = ({ job, onClick }) => {
  const { format: formatCurrency } = useCurrency();
  return (
  <div
    onClick={onClick}
    className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg hover:border-emerald-200 transition-all cursor-pointer"
    data-testid={`job-card-${job.id}`}
  >
    <div className="flex items-start justify-between mb-3">
      <h3 className="font-outfit font-semibold text-lg text-slate-900">{job.title}</h3>
      <span className={`px-2 py-0.5 text-xs rounded-full ${
        job.status === 'open' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
      }`}>
        {job.status}
      </span>
    </div>
    
    <p className="text-slate-500 text-sm mb-3 line-clamp-2">{job.description}</p>
    
    <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
      <span className="flex items-center gap-1">
        <Briefcase className="w-3 h-3" />
        {job.corporate_name || 'Company'}
      </span>
      {job.location && (
        <span className="flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          {job.location}
        </span>
      )}
    </div>

    {job.skills_required?.length > 0 && (
      <div className="flex flex-wrap gap-1.5 mb-4">
        {job.skills_required.slice(0, 3).map(skill => (
          <span key={skill} className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs rounded-full">
            {skill}
          </span>
        ))}
      </div>
    )}

    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
      <div className="flex items-center gap-1 text-sm text-slate-500">
        <Calendar className="w-4 h-4" />
        <span>{new Date(job.start_date).toLocaleDateString()}</span>
      </div>
      <span className="font-semibold text-slate-900">{formatCurrency(job.budget)}</span>
    </div>
  </div>
);
};
